import { Controller, FieldValues, Path, RegisterOptions, useFormContext } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useRulesToDefaultMessage, isPhoneValid } from '@libs/forms';
import { PhoneNumber, TextFieldProps } from '@atoms/inputs';

type FormPhoneNumberProps<T extends FieldValues> = Omit<TextFieldProps, 'name' | 'onChange'> & {
  name: Path<T>;
  requiredMessage?: boolean;
  options?: RegisterOptions<T, Path<T>>;
  region?: string;
};
/**
 * Wrapper around `PhoneNumber` that integrates with react-hook-form.
 */
export function FormPhoneNumber<T extends FieldValues>({
  name,
  required,
  requiredMessage,
  options,
  // we are using only `us` for whole application fo far
  region = 'us',
  ...props
}: FormPhoneNumberProps<T>) {
  const intl = useIntl();
  const { control } = useFormContext<T>();
  const parsedOptions = useRulesToDefaultMessage(options);
  const rules: RegisterOptions<T, Path<T>> = {
    ...parsedOptions,
    validate: (phone: string | null) => {
      if (phone === '' || phone === null || phone === undefined) {
        // Empty field is valid
        return true;
      }
      if (!isPhoneValid(phone, region)) {
        return intl.formatMessage({ id: 'forms.invalidPhoneNumber' });
      }
      return true;
    },
    ...(required
      ? {
          required: requiredMessage ?? intl.formatMessage({ id: 'forms.fieldIsRequired' }),
        }
      : {}),
  };

  return (
    <Controller<T>
      name={name}
      control={control}
      rules={rules}
      render={({ field, fieldState: { error } }) => (
        <PhoneNumber {...props} {...field} required={required} error={!!error} helperText={error?.message} />
      )}
    />
  );
}
